import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { type QueryKey, useQueryClient } from "@msuite/ui/query";
import type { NullableFields, Tables } from "@msuite/utils/types";
import React from "react";
import { client } from "@/hono";
import { PickServiceModal } from "@/modals/organizations/pick-service-modal";
import type { ContactPanelBaseProps } from "../types";

/** Props Interface */
interface ContactServicesMenuProps extends ContactPanelBaseProps {
	queryKey: QueryKey;
}

export function ContactServicesMenu({ queryKey, contactId }: ContactServicesMenuProps) {
	/** Hooks */
	const { t } = useTranslation();
	const pickServiceModal = useDisclosure();
	const qc = useQueryClient();

	/** Functions */
	async function handleOnComplete(service: NullableFields<Tables<"organizationServices">>) {
		try {
			if (!service.id) throw new Error("Service ID is required");
			await client.organizations.services[":serviceId"].partner.$post({
				param: { serviceId: service.id },
				json: {
					contactId,
				},
			});
			await qc.invalidateQueries({ queryKey });
		} catch (error) {
			console.error(error);
		}
	}

	/** Render */
	return (
		<React.Fragment>
			<Button
				variant="primary"
				icon={<Icon name="Plus" />}
				onClick={pickServiceModal.onOpen}
				data-cy="services-add-service-button"
			>
				{t("organization.data.add_service")}
			</Button>
			{pickServiceModal.isOpen && (
				<PickServiceModal
					isOpen={pickServiceModal.isOpen}
					onClose={pickServiceModal.onClose}
					onComplete={handleOnComplete}
				/>
			)}
		</React.Fragment>
	);
}
